import { useEffect, useState } from 'react';
import { SelectedItemType, SparePart } from '../interfaces/Item';
import { sparePartService } from '../services';
import { useErrorCode } from './useErrorCode';
import { useDebounce } from './useDebounce';

interface SearchState {
  loading: boolean;
  loadingParameter: boolean;
  search: string;
  items: SparePart[];
  componentData: SparePart[];
  selectedItem: SelectedItemType;
}
interface SearchActions {
  setSearch: (search: string) => void;
  setSelectedItem: (item: SelectedItemType) => void;
}
export const useSearch = (
  initialArtId: string,
  initialItemId: string
): [SearchState, SearchActions] => {
  const errorContext = useErrorCode();
  const [search, internalSetSearch] = useState('');
  const debouncedSearch = useDebounce(search, CSS_CONFIG.DEBOUNCEDELAYMS);
  const [items, setItems] = useState<SparePart[]>([]);
  const [componentData, setComponentData] = useState<SparePart[]>([]);
  const [selectedItem, setSelectedItem] = useState<SelectedItemType>();
  const [loading, setIsLoading] = useState(false);
  const [loadingParameter, setIsLoadingParameter] = useState(true);

  const normalize = (value: string): string => {
    if (!value) return '';
    const trimmedValue = value.trim();
    if (/^[\d.-\s]+$/.test(trimmedValue)) {
      const digits = trimmedValue.replace(/[^\d]/g, '');
      return digits;
    }
    return trimmedValue;
  };

  useEffect(() => {
    (async (): Promise<void> => {
      try {
        if (initialArtId) {
          const result =
            await sparePartService.fetchSparePartItemsFromArticleId(
              initialArtId
            );
          setItems(result);
        } else if (initialItemId) {
          const result = await sparePartService.fetchSparePartId(
            parseInt(initialItemId, 10)
          );
          setSelectedItem(result);
        }
      } catch (e) {
        // Do stuff
      } finally {
        setIsLoadingParameter(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async (): Promise<void> => {
      try {
        setIsLoading(true);
        const result = await sparePartService.searchSpareParts(debouncedSearch);
        setItems(result);
        if (search.length >= 8) {
          const resultComponent =
            await sparePartService.fetchSparePartRelatedToArticleId(
              debouncedSearch
            );
          setComponentData(resultComponent);
        }
      } catch (e: any) {
        errorContext.setErrorCode({
          status: e.status,
        });
      } finally {
        setIsLoading(false);
      }
    })();
  }, [debouncedSearch]);

  return [
    {
      search,
      loading,
      loadingParameter,
      selectedItem,
      items,
      componentData,
    },
    {
      setSearch: (value: string) => internalSetSearch(normalize(value)),
      setSelectedItem,
    },
  ];
};
